import React, { useCallback, useEffect } from 'react';
import { noop } from 'lodash';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '../../../core/components/modal/Modal';
import { BulkEditLocationRequest, EditLocationSchema, PartRequestBulkItemDto } from '../types';
import { createLocationRequest } from './utils';
import Input from '../../../core/components/input/Input';
import ButtonActions from '../../../core/components/button-actions/ButtonActions';
import { DialogSection, SelectionType } from '../SelectionForm';
import { useEditLocation } from './useEditLocation';

interface EditLocationDialogProps {
  onClose?: (nextItem: null) => void;
  row: PartRequestBulkItemDto | null;
}

export const EditLocationDialog = ({ onClose = noop, row }: EditLocationDialogProps) => {
  const form = useForm<BulkEditLocationRequest>({
    resolver: zodResolver(EditLocationSchema),
  });

  const { reset, handleSubmit } = form;

  useEffect(() => {
    if (row) {
      reset(createLocationRequest(row));
    }
  }, [row, reset]);

  const handleClose = useCallback(() => {
    onClose(null);
  }, [onClose]);

  const [pending, updateLocation] = useEditLocation(handleClose);

  const handleSave = useCallback(() => {
    handleSubmit(updateLocation)();
  }, [handleSubmit, updateLocation]);

  return (
    <FormProvider {...form}>
      <Modal visible={!!row} title="Edit Location" onCancel={handleClose} destroyOnClose>
        <DialogSection>

          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Input label="Location" onChange={onChange} value={value ?? ''} error={error?.message} />
            )}
            name="location"
          />

          <SelectionType lineId={row?.lineId} />

        </DialogSection>
        <ButtonActions
          cancelLabel="Cancel"
          createLabel="Save"
          createType="button"
          cancelClick={handleClose}
          createClick={handleSave}
          offsetTop
          isLoading={pending}
        />
      </Modal>
    </FormProvider>
  );
};
